import React, { useState } from "react";
import { toast } from "sonner";
import { X, User as UserIcon, Lock } from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";

export default function SettingsModal({ open, onClose }) {
  const { t } = useI18n();
  const { user, refresh } = useAuth();
  const [tab, setTab] = useState("profile");
  const [busy, setBusy] = useState(false);

  const [profile, setProfile] = useState({
    name: user?.name || "",
    bio: user?.bio || "",
    location: user?.location || "",
  });
  const [creds, setCreds] = useState({
    current_password: "",
    new_email: "",
    new_password: "",
    confirm_new_password: "",
  });

  if (!open) return null;

  const saveProfile = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch("/auth/me", {
        name: profile.name,
        bio: profile.bio || null,
        location: profile.location || null,
      });
      toast.success(t("saved"));
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error_generic"));
    } finally {
      setBusy(false);
    }
  };

  const saveCredentials = async (e) => {
    e.preventDefault();
    if (creds.new_password && creds.new_password !== creds.confirm_new_password) {
      toast.error(t("passwords_no_match"));
      return;
    }
    if (!creds.new_email && !creds.new_password) {
      toast.error(t("nothing_to_change"));
      return;
    }
    setBusy(true);
    try {
      await api.patch("/auth/credentials", {
        current_password: creds.current_password,
        new_email: creds.new_email || null,
        new_password: creds.new_password || null,
      });
      toast.success(t("saved"));
      setCreds({ current_password: "", new_email: "", new_password: "", confirm_new_password: "" });
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error_generic"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[10005] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        data-testid="settings-modal"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-white rounded-2xl shadow-2xl max-h-[90vh] flex flex-col"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E6E2D8]">
          <div>
            <div className="overline text-[#143122]">{t("account")}</div>
            <h2 className="mt-1 font-serif-display text-2xl text-[#143122]">
              {t("settings")}
            </h2>
          </div>
          <button
            type="button"
            data-testid="settings-close"
            onClick={onClose}
            className="text-[#1A1F1C]/50 hover:text-[#143122]"
            aria-label="Close"
          >
            <X size={18} strokeWidth={1.75} />
          </button>
        </div>

        <div className="flex border-b border-[#E6E2D8]">
          <button
            type="button"
            data-testid="settings-tab-profile"
            onClick={() => setTab("profile")}
            className={`flex-1 py-3 text-sm font-medium inline-flex items-center justify-center gap-2 transition-colors ${
              tab === "profile" ? "text-[#143122] border-b-2 border-[#143122]" : "text-[#1A1F1C]/50"
            }`}
          >
            <UserIcon size={14} strokeWidth={1.75} /> {t("profile")}
          </button>
          <button
            type="button"
            data-testid="settings-tab-security"
            onClick={() => setTab("security")}
            className={`flex-1 py-3 text-sm font-medium inline-flex items-center justify-center gap-2 transition-colors ${
              tab === "security" ? "text-[#143122] border-b-2 border-[#143122]" : "text-[#1A1F1C]/50"
            }`}
          >
            <Lock size={14} strokeWidth={1.75} /> {t("security")}
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-5">
          {tab === "profile" ? (
            <form onSubmit={saveProfile} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1.5">{t("name")}</label>
                <input
                  data-testid="settings-name"
                  required
                  className="kav-input"
                  value={profile.name}
                  onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">{t("location")}</label>
                <input
                  data-testid="settings-location"
                  className="kav-input"
                  value={profile.location}
                  onChange={(e) => setProfile({ ...profile, location: e.target.value })}
                  placeholder="Helsinki, FI"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">{t("bio")}</label>
                <textarea
                  data-testid="settings-bio"
                  rows={5}
                  className="kav-input"
                  value={profile.bio}
                  onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                  placeholder={t("bio_placeholder")}
                />
                <div className="mt-1 text-xs text-[#1A1F1C]/50">
                  {profile.bio.length}/500
                </div>
              </div>
              <button
                type="submit"
                data-testid="settings-save-profile"
                disabled={busy}
                className="kav-btn-primary text-sm w-full justify-center disabled:opacity-50"
              >
                {busy ? "..." : t("save")}
              </button>
            </form>
          ) : (
            <form onSubmit={saveCredentials} className="space-y-4">
              <div className="text-xs text-[#1A1F1C]/70 bg-[#F4F1E9] rounded p-3">
                {t("security_hint")}
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">{t("current_password")}</label>
                <input
                  data-testid="settings-current-password"
                  required
                  type="password"
                  className="kav-input"
                  value={creds.current_password}
                  onChange={(e) => setCreds({ ...creds, current_password: e.target.value })}
                />
              </div>
              <div className="border-t border-[#E6E2D8] pt-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1.5">{t("new_email")}</label>
                  <input
                    data-testid="settings-new-email"
                    type="email"
                    className="kav-input"
                    value={creds.new_email}
                    onChange={(e) => setCreds({ ...creds, new_email: e.target.value })}
                    placeholder={user?.email}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5">{t("new_password")}</label>
                  <input
                    data-testid="settings-new-password"
                    type="password"
                    minLength={6}
                    className="kav-input"
                    value={creds.new_password}
                    onChange={(e) => setCreds({ ...creds, new_password: e.target.value })}
                  />
                </div>
                {creds.new_password && (
                  <div>
                    <label className="block text-sm font-medium mb-1.5">{t("confirm_new_password")}</label>
                    <input
                      data-testid="settings-confirm-password"
                      type="password"
                      minLength={6}
                      className="kav-input"
                      value={creds.confirm_new_password}
                      onChange={(e) => setCreds({ ...creds, confirm_new_password: e.target.value })}
                    />
                  </div>
                )}
              </div>
              <button
                type="submit"
                data-testid="settings-save-credentials"
                disabled={busy}
                className="kav-btn-primary text-sm w-full justify-center disabled:opacity-50"
              >
                {busy ? "..." : t("save")}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
